import { useEffect, useState } from 'react';
import { useEventStore } from '../../../stores/eventStore';

const STATUS_COLORS: Record<string, string> = {
  ASSIGNED: 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30',
  CONFIRMED: 'bg-green-500/20 text-green-300 border border-green-500/30',
  DECLINED: 'bg-red-500/20 text-red-300 border border-red-500/30',
};

const STATUS_LABELS: Record<string, string> = {
  ASSIGNED: 'Pendente',
  CONFIRMED: 'Confirmado',
  DECLINED: 'Recusado',
};

interface Props {
  teamId: string;
}

export default function MySchedule({ teamId }: Props) {
  const { myAssignments, loadMyAssignments, respondAssignment, isLoading } = useEventStore();
  const [declineId, setDeclineId] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState('');

  useEffect(() => {
    loadMyAssignments(teamId);
  }, [teamId, loadMyAssignments]);

  const handleConfirm = async (eventId: string, assignmentId: string) => {
    await respondAssignment(teamId, eventId, assignmentId, 'CONFIRMED');
  };

  const handleDecline = async (eventId: string, assignmentId: string) => {
    await respondAssignment(teamId, eventId, assignmentId, 'DECLINED', declineReason);
    setDeclineId(null);
    setDeclineReason('');
  };

  if (isLoading) {
    return <div className="py-12 text-center text-white/30 text-sm">Carregando...</div>;
  }

  if (myAssignments.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-white/40 text-sm">Você não tem atribuições no período</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {myAssignments.map((a) => (
        <div key={a.id} className="bg-white/[0.03] border border-white/10 rounded-2xl px-5 py-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-medium text-white">{a.instrument_name}</p>
              <p className="text-xs text-white/40 mt-0.5">
                {a.event_name} · {new Date(a.event_date + 'T12:00:00').toLocaleDateString('pt-BR')}
              </p>
              {a.event_status === 'CANCELLED' && (
                <span className="mt-1 inline-block px-2 py-0.5 bg-red-500/20 border border-red-500/30 text-red-400 text-xs rounded-md">
                  Evento cancelado
                </span>
              )}
            </div>
            <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${STATUS_COLORS[a.status]}`}>
              {STATUS_LABELS[a.status]}
            </span>
          </div>

          {a.status === 'ASSIGNED' && a.event_status !== 'CANCELLED' && (
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => handleConfirm(a.event_id, a.id)}
                className="flex-1 py-2 bg-green-500/20 border border-green-500/30 text-green-300 rounded-lg text-xs font-medium hover:bg-green-500/30 transition-colors"
              >
                Confirmar
              </button>
              <button
                onClick={() => setDeclineId(a.id)}
                className="flex-1 py-2 bg-red-500/20 border border-red-500/30 text-red-300 rounded-lg text-xs font-medium hover:bg-red-500/30 transition-colors"
              >
                Recusar
              </button>
            </div>
          )}

          {declineId === a.id && (
            <div className="mt-3 space-y-2">
              <input
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Motivo (opcional)"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => setDeclineId(null)}
                  className="flex-1 py-2 border border-white/10 text-white/50 rounded-lg text-xs"
                >
                  Voltar
                </button>
                <button
                  onClick={() => handleDecline(a.event_id, a.id)}
                  className="flex-1 py-2 bg-red-500/20 border border-red-500/30 text-red-300 rounded-lg text-xs font-medium"
                >
                  Confirmar Recusa
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
